import React, { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast, ToastContainer } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useTitleCount } from './TitleCountContext';
import { useFfufCount } from './FfufCountContext';
import { useLiveDomainCount } from './LiveDomainCountContext';
import { useNucleiCount } from './NucleiCountContext';
import { useAclBypassCount } from './AclBypassCountContext';
import { useVulnerabilityFindingsCount } from './VulnerabilityFindingsCountContext';
import { useSecretsCount } from './SecretsCountContext';

// Keys for localStorage
const PREVIOUS_COUNTS_KEY = 'previous_notification_counts';
const LAST_POLL_TIME_KEY = 'last_notification_poll_time';
const LAST_ACKNOWLEDGED_KEY = 'last_acknowledged_time';
const LAST_PROCESSED_COUNTS_KEY = 'last_processed_counts';

// Create the context
const NotificationContext = createContext();

// Add typeMap constant before the NotificationProvider
const typeMap = {
  title: {
    label: 'Title Change',
    description: count => `${count} new title ${count === 1 ? 'change' : 'changes'} detected`
  },
  ffuf: {
    label: 'FFUF Result',
    description: count => `${count} new FFUF ${count === 1 ? 'result' : 'results'} available`
  },
  liveDomain: {
    label: 'Live Domain',
    description: count => `${count} new live ${count === 1 ? 'domain' : 'domains'} discovered`
  },
  nuclei: {
    label: 'Nuclei Scan',
    description: count => `${count} new nuclei scan ${count === 1 ? 'result' : 'results'} available`
  },
  aclBypass: {
    label: 'ACL Bypass',
    description: count => `${count} new ACL ${count === 1 ? 'bypass' : 'bypasses'} detected`
  },
  vulnerability: {
    label: 'Vulnerability',
    description: count => `${count} new ${count === 1 ? 'vulnerability' : 'vulnerabilities'} found`
  },
  secrets: {
    label: 'Secret',
    description: count => `${count} new ${count === 1 ? 'secret' : 'secrets'} discovered`
  }
};

// Define stable toast IDs for each notification type
const stableToastIds = {
  title: 'title-notification',
  ffuf: 'ffuf-notification',
  liveDomain: 'live-domain-notification',
  nuclei: 'nuclei-scan-notification',
  aclBypass: 'acl-bypass-notification',
  vulnerability: 'vulnerability-notification',
  secrets: 'secrets-notification'
};

// Define navigation paths for each notification type
const navigationPaths = {
  title: '/new-titles',
  ffuf: '/ffuf-output',
  liveDomain: '/domain-live-stream',
  nuclei: '/nuclei-scan',
  aclBypass: '/acl-bypasses',
  vulnerability: '/vulnerability-findings',
  secrets: '/secrets-live-stream'
};

export function NotificationProvider({ children }) {
  const navigate = useNavigate();
  // All notifications that will display in the notification popover
  const [notifications, setNotifications] = useState([]);
  // Overall unread notification count for badge
  const [unreadCount, setUnreadCount] = useState(0);
  // Track if there are new notifications since last view
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  // Track when notifications were last acknowledged by the user
  const [lastAcknowledgedTime, setLastAcknowledgedTime] = useState(() => {
    try {
      const storedTime = localStorage.getItem(LAST_ACKNOWLEDGED_KEY);
      // If no stored time, use current time to prevent initial notifications flood
      if (!storedTime) {
        const now = Date.now();
        localStorage.setItem(LAST_ACKNOWLEDGED_KEY, now.toString());
        return now;
      }
      return parseInt(storedTime, 10);
    } catch (error) {
      console.error('Failed to parse last acknowledged time from localStorage', error);
      // If there's an error, set current time as last acknowledged
      const now = Date.now();
      localStorage.setItem(LAST_ACKNOWLEDGED_KEY, now.toString());
      return now;
    }
  });
  
  // Track browser visibility state
  const [isTabActive, setIsTabActive] = useState(document.visibilityState === 'visible');
  // Queue for notifications when tab is not active - simple array of objects
  const notificationQueue = useRef([]);
  
  // Track if we're in the initial mounting phase
  const isInitialMount = useRef(true);

  // Initialize lastProcessedCounts from localStorage with proper error handling
  const lastProcessedCounts = useRef((() => {
    const stored = localStorage.getItem(LAST_PROCESSED_COUNTS_KEY);
    if (!stored) return {};
    try {
      const parsed = JSON.parse(stored);
      return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch {
      console.error('Failed to parse last processed counts, resetting to empty object');
      return {};
    }
  })());  // Note the extra () to execute the function

  // Get counts from all contexts
  const { count: titleCount, loading: titleLoading } = useTitleCount();
  const { count: ffufCount, loading: ffufLoading } = useFfufCount();
  const { count: liveDomainCount, loading: liveDomainLoading } = useLiveDomainCount();
  const { count: nucleiCount, loading: nucleiLoading } = useNucleiCount();
  const { count: aclBypassCount, loading: aclBypassLoading } = useAclBypassCount();
  const { count: vulnerabilityCount, loading: vulnerabilityLoading } = useVulnerabilityFindingsCount();
  const { count: secretsCount, loading: secretsLoading } = useSecretsCount();

  // Combine loading states
  const isLoadingCounts = titleLoading || ffufLoading || liveDomainLoading || nucleiLoading || aclBypassLoading || vulnerabilityLoading || secretsLoading;

  // Function to handle clicking on a notification
  const handleNotificationClick = useCallback((notification) => {
    const toastId = stableToastIds[notification.type];
    
    // Dismiss with specific reason
    toast.dismiss(toastId, { reason: 'navigation' });

    // Mark the notification as read if it isn't already
    if (notification.isUnRead) {
      const updatedNotifications = notifications.map(n => 
        n.id === notification.id ? { ...n, isUnRead: false } : n
      );
      
      setNotifications(updatedNotifications);
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      // Update localStorage
      try {
        localStorage.setItem('notifications', JSON.stringify(updatedNotifications));
      } catch (error) {
        console.error('Failed to save updated notifications to localStorage', error);
      }
    }
    
    // Navigate based on notification type
    const path = navigationPaths[notification.type] || '/overview';
    navigate(path);
    
    console.log(`Navigating to: ${path}`);
  }, [notifications, navigate]);

  // Set up visibility change detection
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === 'visible';
      console.log(`Tab visibility changed: ${isVisible ? 'visible' : 'hidden'}`);
      setIsTabActive(isVisible);
      
      // Process queued notifications when tab becomes visible again
      if (isVisible && notificationQueue.current.length > 0) {
        console.log(`Processing ${notificationQueue.current.length} queued notifications`);
        
        // Get current counts
        const currentCounts = {
          title: titleCount,
          ffuf: ffufCount,
          liveDomain: liveDomainCount,
          nuclei: nucleiCount,
          aclBypass: aclBypassCount,
          vulnerability: vulnerabilityCount,
          secrets: secretsCount
        };
        
        // Get previous counts
        let previousCounts;
        try {
          const storedCounts = localStorage.getItem(PREVIOUS_COUNTS_KEY);
          previousCounts = storedCounts ? JSON.parse(storedCounts) : {
            title: 0, ffuf: 0, liveDomain: 0, nuclei: 0, 
            aclBypass: 0, vulnerability: 0, secrets: 0
          };
        } catch (error) {
          console.error('Error parsing previous counts:', error);
          previousCounts = {
            title: 0, ffuf: 0, liveDomain: 0, nuclei: 0, 
            aclBypass: 0, vulnerability: 0, secrets: 0
          };
        }
        
        // Check if counts have actually changed
        const hasChanges = Object.keys(currentCounts).some(
          type => currentCounts[type] !== previousCounts[type]
        );
        
        if (!hasChanges) {
          console.log('No changes in counts, skipping queued notifications');
          notificationQueue.current = [];
          return;
        }
        
        // Process notifications (with a limit)
        const toProcess = notificationQueue.current.slice(0, 3);
        notificationQueue.current = notificationQueue.current.slice(3);
        
        toProcess.forEach(notification => {
          try {
            toast(notification.content, {
              type: notification.type,
              toastId: notification.id,
              closeOnClick: false,
              autoClose: false
            });
          } catch (error) {
            console.error('Error showing toast:', error);
          }
        });
        
        // Update previous counts after showing notifications
        try {
          localStorage.setItem(PREVIOUS_COUNTS_KEY, JSON.stringify(currentCounts));
        } catch (error) {
          console.error('Error saving counts:', error);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [titleCount, ffufCount, liveDomainCount, nucleiCount, aclBypassCount, vulnerabilityCount, secretsCount]);
  
  // Initialize notifications on first load
  useEffect(() => {
    console.log('Initializing notifications from localStorage');
    try {
      const storedNotifications = localStorage.getItem('notifications');
      
      if (storedNotifications) {
        const parsedNotifications = JSON.parse(storedNotifications);
        
        // Only load if there are actually notifications to load
        if (Array.isArray(parsedNotifications) && parsedNotifications.length > 0) {
          // Ensure all notifications have properly formatted createdAt dates
          const validNotifications = parsedNotifications.map(notification => ({
            ...notification,
            createdAt: new Date(notification.createdAt),
            // Ensure notifications older than last acknowledgment are marked as read
            isUnRead: notification.isUnRead && new Date(notification.createdAt).getTime() > lastAcknowledgedTime
          }));
          
          setNotifications(validNotifications);
          const unreadCount = validNotifications.filter(n => n.isUnRead).length;
          setUnreadCount(unreadCount);
          setHasNewNotifications(unreadCount > 0);
          console.log('Loaded notifications from localStorage:', validNotifications.length);
        }
      }
    } catch (error) {
      console.error('Failed to load notifications from localStorage', error);
    }
  }, [lastAcknowledgedTime]);

  // Save notifications to localStorage when they change
  useEffect(() => {
    try {
      if (notifications.length > 0) {
        localStorage.setItem('notifications', JSON.stringify(notifications));
      } else {
        localStorage.removeItem('notifications');
      }
    } catch (error) {
      console.error('Failed to save notifications to localStorage', error);
    }
  }, [notifications]);

  // Check for count increases and create notifications
  useEffect(() => {
    // Don't run if counts are still loading or on initial mount
    if (isLoadingCounts || isInitialMount.current) {
      if (isInitialMount.current) {
        isInitialMount.current = false;
        console.log("Initial mount, skipping notification check until counts stabilize.");
      } else {
        console.log("Counts are loading, skipping notification check.");
      }
      return;
    }
    
    // Current counts
    const currentCounts = {
      title: titleCount,
      ffuf: ffufCount,
      liveDomain: liveDomainCount,
      vulnerability: vulnerabilityCount,
      nuclei: nucleiCount,
      aclBypass: aclBypassCount,
      secrets: secretsCount
    };

    // Initialize lastProcessedCounts if empty
    if (Object.keys(lastProcessedCounts.current).length === 0) {
      console.log("Initializing lastProcessedCounts with current counts");
      lastProcessedCounts.current = currentCounts;
      try {
        localStorage.setItem(LAST_PROCESSED_COUNTS_KEY, JSON.stringify(currentCounts));
      } catch (error) {
        console.error('Failed to save initial counts to localStorage', error);
      }
      return;
    }

    // Log current state for debugging
    console.log('Current counts:', currentCounts);
    console.log('Last processed counts:', lastProcessedCounts.current);
    
    let hasChanges = false;
    
    // Check each count for increases
    Object.keys(currentCounts).forEach(type => {
      if (!typeMap[type]) return;
      
      const current = currentCounts[type];
      const lastProcessed = lastProcessedCounts.current[type] || 0;

      // Only create notification if count increased compared to lastProcessed
      if (current > lastProcessed) {
        hasChanges = true;
        const increment = current - lastProcessed;
        
        console.log(`Count increased for ${type}: ${lastProcessed} -> ${current}. Creating notification.`);
        
        // Create a notification for this increase
        const notification = {
          id: `${type}_${Date.now()}_${Math.floor(Math.random() * 10000)}`,
          title: `${increment} ${typeMap[type].label} found`,
          description: typeMap[type].description(increment),
          type,
          createdAt: new Date(),
          isUnRead: true
        };
        
        // Add to state
        setNotifications(prev => [notification, ...prev]);
        setUnreadCount(prev => prev + 1);
        setHasNewNotifications(true);
        
        // Create toast content
        const content = (
          <div 
            onClick={() => handleNotificationClick(notification)} 
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleNotificationClick(notification);
              }
            }}
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
          >
            <strong>{notification.title}</strong>
            <p>{notification.description}</p>
          </div>
        );
        
        // Determine toast type
        const toastType = 
          type === 'title' ? 'info' : 
          type === 'ffuf' ? 'success' : 
          type === 'liveDomain' ? 'success' : 
          type === 'nuclei' ? 'warning' : 
          type === 'aclBypass' ? 'warning' : 
          type === 'vulnerability' ? 'error' : 'info';
        
        // Show toast if tab is active, otherwise queue it
        if (isTabActive) {
          try {
            // Check if a toast with this ID already exists
            if (!toast.isActive(stableToastIds[type])) {
              toast(content, {
                type: toastType,
                toastId: stableToastIds[type],
                closeOnClick: false,
                autoClose: false,
                onClose: (closureReason) => {
                  console.log('Toast closed due to:', closureReason);
                }
              });
            }
          } catch (error) {
            console.error('Error showing toast:', error);
          }
        } else if (!notificationQueue.current.some(n => n.id === stableToastIds[type])) {
          // Only queue if not already queued
          notificationQueue.current.push({
            content,
            type: toastType,
            id: stableToastIds[type]
          });
        }
      }
    });
    
    // Only update storage if there were actual changes
    if (hasChanges) {
      // Update lastProcessedCounts and save to localStorage
      lastProcessedCounts.current = currentCounts;
      try {
        localStorage.setItem(LAST_PROCESSED_COUNTS_KEY, JSON.stringify(currentCounts));
      } catch (error) {
        console.error('Error saving counts:', error);
      }
    } else {
      console.log('No changes detected in counts');
    }
    
  }, [titleCount, ffufCount, liveDomainCount, vulnerabilityCount, nucleiCount, aclBypassCount, secretsCount, 
      handleNotificationClick, isTabActive, isLoadingCounts]);

  // Function to acknowledge all notifications (mark as read)
  const acknowledgeNotifications = useCallback(() => {
    console.log('Acknowledging all notifications');
    
    const now = Date.now();
    
    // Mark all notifications as read
    const updatedNotifications = notifications.map(notification => ({
      ...notification,
      isUnRead: false
    }));
    
    setNotifications(updatedNotifications);
    setUnreadCount(0);
    setHasNewNotifications(false);
    
    // Update localStorage
    try {
      localStorage.setItem('notifications', JSON.stringify(updatedNotifications));
      
      // Update the last acknowledged time
      localStorage.setItem(LAST_ACKNOWLEDGED_KEY, now.toString());
      setLastAcknowledgedTime(now);
      
      // Also update last processed counts to current to prevent duplicate notifications
      const currentCounts = {
        title: titleCount,
        ffuf: ffufCount,
        liveDomain: liveDomainCount,
        vulnerability: vulnerabilityCount,
        nuclei: nucleiCount,
        aclBypass: aclBypassCount,
        secrets: secretsCount
      };
      
      lastProcessedCounts.current = currentCounts;
      localStorage.setItem(LAST_PROCESSED_COUNTS_KEY, JSON.stringify(currentCounts));
    } catch (error) {
      console.error('Error saving acknowledged notifications:', error);
    }
    
  }, [notifications, titleCount, ffufCount, liveDomainCount, vulnerabilityCount, nucleiCount, aclBypassCount, secretsCount]);

  // Function to clear all notifications
  const clearAllNotifications = useCallback(() => {
    console.log('Clearing all notifications');
    
    setNotifications([]);
    setUnreadCount(0);
    setHasNewNotifications(false);
    
    // Clear from localStorage
    try {
      localStorage.removeItem('notifications');
      
      // Update the last acknowledged time
      const now = Date.now();
      localStorage.setItem(LAST_ACKNOWLEDGED_KEY, now.toString());
      setLastAcknowledgedTime(now);
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
    
  }, []);

  // For backwards compatibility
  const markAllAsRead = useCallback(() => {
    acknowledgeNotifications();
  }, [acknowledgeNotifications]);

  // Use useMemo to memoize the context value
  const contextValue = useMemo(() => ({
    notifications,
    unreadCount,
    hasNewNotifications,
    addNotification: null, // Removing this function as we're handling notifications automatically
    markAllAsRead,
    acknowledgeNotifications,
    clearAllNotifications,
    lastAcknowledgedTime,
    handleNotificationClick
  }), [notifications, unreadCount, hasNewNotifications, markAllAsRead, acknowledgeNotifications, clearAllNotifications, lastAcknowledgedTime, handleNotificationClick]);

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <ToastContainer
        position="top-right"
        autoClose={false}
        newestOnTop
        closeOnClick={false}
        limit={3}
        stacked
        toastStyle={{
          backgroundColor: 'rgba(40, 44, 52, 0.95)',
          color: '#fff',
          padding: '16px',
          fontFamily: 'inherit',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '8px',
          backdropFilter: 'blur(8px)',
          boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
        }}
        onClose={({ reason }) => {
          if (reason === 'navigation') {
            // Handle navigation-triggered dismissals
          }
        }}
      />
    </NotificationContext.Provider>
  );
}

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
} 